/**
 * What the Deal Room pages can do to a deal and its contents — the same set
 * the sidebar's Deal Room offers: add files (button or drop), delete a file,
 * delete a folder or the whole deal. Every delete is confirmed first.
 *
 * Nothing here edits a local tree: each change announces
 * `USER_FILES_UPDATED_EVENT`, which both the sidebar and `useFileLibrary`
 * re-read on.
 *
 * The host renders `element` once (the confirmation dialogs).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import DeleteFileDialog from "../components/AddFiles/DeleteFileDialog";
import DeleteFolderDialog from "../components/Files/DeleteFolderDialog";
import { USER_FILES_UPDATED_EVENT } from "../constants/events";
import {
	type ClassificationStatusMap,
	classificationFinishedSince,
	EMPTY_STATUSES,
	useClassificationStatuses,
} from "./useClassificationStatuses";
import { canAddFilesTo, useAddFiles } from "../context/AddFilesContext";
import { useToast } from "../context/ToastContext";
import { useUploadManager } from "../context/UploadManager";
import {
	deleteFile,
	deleteFolder,
	type FileInfo,
	type FolderTreeNode,
} from "../services/fileApi";
import { countDealFiles } from "../utils/addFilesReview";
import { pickedFromDrop } from "../utils/pickedFiles";

export interface FolderToDelete {
	node: FolderTreeNode;
	/** The deal itself, rather than one of its sub-folders. */
	isDeal: boolean;
	/** The deal's root folder id (the node's own id when `isDeal`). */
	dealId: number;
}

export interface FileToDelete {
	file: FileInfo;
	dealId: number;
	dealName: string;
}

/** Files still uploading or being read into a deal. */
export interface DealActivity {
	busy: boolean;
	/** Files Doppler is still reading, when known. */
	readingCount: number;
}

export interface DealRoomActions {
	/** Opens the add-files review; undefined when this deal can't take files. */
	addFilesTo: (deal: FolderTreeNode) => ((files?: File[]) => void) | undefined;
	/** Drop handler for a deal: the dropped files go to the add-files review. */
	dropFilesOn: (deal: FolderTreeNode, event: React.DragEvent) => void;
	/** An upload into this deal is running — deletes wait for it. */
	isUploadingInto: (dealId: number) => boolean;
	requestDeleteFolder: (target: FolderToDelete) => void;
	requestDeleteFile: (target: FileToDelete) => void;
	element: React.ReactNode;
}

const announce = (detail: Record<string, unknown>) =>
	window.dispatchEvent(new CustomEvent(USER_FILES_UPDATED_EVENT, { detail }));

/** A drag carries files (not text or a link from the page). */
export const dragHasFiles = (event: React.DragEvent): boolean =>
	Array.from(event.dataTransfer.types).includes("Files");

export function useDealRoomActions(
	options: {
		onFolderDeleted?: (target: FolderToDelete) => void;
		onFileDeleted?: (target: FileToDelete) => void;
	} = {},
): DealRoomActions {
	const { onFolderDeleted, onFileDeleted } = options;
	const addFiles = useAddFiles();
	const { uploadingFolderIds } = useUploadManager();
	const { showToast } = useToast();
	const [folderToDelete, setFolderToDelete] = useState<FolderToDelete | null>(
		null,
	);
	const [fileToDelete, setFileToDelete] = useState<FileToDelete | null>(null);

	const isUploadingInto = useCallback(
		(dealId: number) => uploadingFolderIds.has(dealId),
		[uploadingFolderIds],
	);

	const addFilesTo = useCallback(
		(deal: FolderTreeNode) =>
			addFiles && canAddFilesTo(deal)
				? (files?: File[]) => addFiles.openAddFiles({ deal, files })
				: undefined,
		[addFiles],
	);

	const dropFilesOn = useCallback(
		(deal: FolderTreeNode, event: React.DragEvent) => {
			const open = addFilesTo(deal);
			if (!open || !dragHasFiles(event)) return;
			event.preventDefault();
			void pickedFromDrop(event.dataTransfer).then(({ files, problems }) => {
				if (problems.length > 0) showToast(problems.join(" "), "info");
				if (files.length > 0) open(files);
			});
		},
		[addFilesTo, showToast],
	);

	const confirmDeleteFolder = async (target: FolderToDelete) => {
		setFolderToDelete(null);
		const { node, isDeal, dealId } = target;
		// Same rule as a file delete: an upload into the deal writes into its
		// folders, and deleting one under it leaves the upload half-landed.
		if (uploadingFolderIds.has(dealId)) {
			showToast(
				"Files are still being added to this deal. Try again once that finishes.",
				"info",
			);
			return;
		}
		try {
			await deleteFolder(node.folder.id);
			showToast(`Deleted ${node.folder.name}.`, "success");
			onFolderDeleted?.(target);
			announce({ source: "delete-folder", folderId: node.folder.id, isDeal });
		} catch (error) {
			console.error("Failed to delete folder:", error);
			showToast(`Couldn't delete ${node.folder.name}. Please try again.`);
		}
	};

	const confirmDeleteFile = async (target: FileToDelete) => {
		const { file, dealId } = target;
		setFileToDelete(null);
		// The delete button is disabled during an upload into this deal; this
		// is the backstop, since the upload could re-send the same file.
		if (uploadingFolderIds.has(dealId)) {
			showToast(
				"Files are still being added to this deal. Try again once that finishes.",
				"info",
			);
			return;
		}
		try {
			await deleteFile(file.id);
			showToast(`Deleted ${file.filename}.`, "success");
			onFileDeleted?.(target);
			announce({ source: "delete-file", fileId: file.id });
		} catch (error) {
			console.error("Failed to delete file:", error);
			showToast(`Couldn't delete ${file.filename}. Please try again.`);
		}
	};

	const element = (
		<>
			{folderToDelete && (
				<DeleteFolderDialog
					name={folderToDelete.node.folder.name}
					isDeal={folderToDelete.isDeal}
					fileCount={countDealFiles(folderToDelete.node)}
					onCancel={() => setFolderToDelete(null)}
					onConfirm={() => void confirmDeleteFolder(folderToDelete)}
				/>
			)}
			{fileToDelete && (
				<DeleteFileDialog
					fileName={fileToDelete.file.filename}
					dealName={fileToDelete.dealName}
					onCancel={() => setFileToDelete(null)}
					onConfirm={() => void confirmDeleteFile(fileToDelete)}
				/>
			)}
		</>
	);

	return {
		addFilesTo,
		dropFilesOn,
		isUploadingInto,
		requestDeleteFolder: setFolderToDelete,
		requestDeleteFile: setFileToDelete,
		element,
	};
}

/**
 * Which deals are busy — an upload running into them, or Doppler still
 * reading their files — from the same two sources the sidebar spins on. Each
 * time a deal's count of files still being read drops, `onReadProgress` fires
 * so the page can re-read the tree and move rows from "Reading…" to ready.
 */
export function useDealActivity(onReadProgress: () => void) {
	const { uploadingFolderIds } = useUploadManager();
	const statuses = useClassificationStatuses();

	// Re-read only when a deal has fewer files still being read. The first
	// poll answer and the empty "not known" map are not progress: treating
	// them as one re-fetched the whole tree on every visit.
	const lastStatusesRef = useRef<ClassificationStatusMap>(EMPTY_STATUSES);
	const onReadProgressRef = useRef(onReadProgress);
	onReadProgressRef.current = onReadProgress;
	useEffect(() => {
		if (statuses === EMPTY_STATUSES) return;
		const previous = lastStatusesRef.current;
		lastStatusesRef.current = statuses;
		if (classificationFinishedSince(previous, statuses)) {
			onReadProgressRef.current();
		}
	}, [statuses]);

	return useCallback(
		(dealId: number, projectId: string | null | undefined): DealActivity => {
			const classification = projectId ? statuses.get(projectId) : undefined;
			return {
				busy:
					uploadingFolderIds.has(dealId) || Boolean(classification?.isProcessing),
				readingCount: classification?.processingCount ?? 0,
			};
		},
		[statuses, uploadingFolderIds],
	);
}
